import React, { useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { ArrowUp, MessageCircleQuestion, Minus, Plus } from 'lucide-react';

import {
	useAskConciergeMutation,
	useOpenConciergeTicketMutation,
} from '../api/concierge';
import { useAuth } from '../providers/AppAuthProvider';

import { CONCIERGE_OPEN_EVENT } from './conciergeEvents';

import type { ConciergeTurn } from '../api/concierge';
import type { ConciergeOpenDetail } from './conciergeEvents';

type PageContext = {
	page: string;
	context: NonNullable<ConciergeOpenDetail['context']>;
};

type Message = ConciergeTurn & { escalate?: boolean };

const MAX_TURNS = 20;

// v9 --v9-navy (#071a40) and its hover (#0b2a66): the Concierge's own colours, not in the theme palette.
const NAVY = 'bg-[#071a40] hover:bg-[#0b2a66]';
const HEADER_BUTTON =
	'grid h-11 w-11 place-items-center rounded-[10px] hover:bg-white/[.14] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-[3px] focus-visible:outline-white';
const BTN_SM =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 py-1 text-xs font-medium leading-[18px] transition-colors';
const INPUT =
	'block w-full rounded-lg border border-gray-300 bg-white py-2 text-sm leading-5 text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none';
const BUBBLE = 'max-w-[92%] px-3 py-2.5 text-sm leading-normal';
const BOT =
	'self-start rounded-xl rounded-bl border border-gray-200 bg-white text-gray-900';
const ME = 'self-end rounded-xl rounded-br bg-[#071a40] text-white';

const SUGGESTIONS: Record<string, string[]> = {
	statements: [
		'Which cases wait on merchants?',
		'Which cases need attention?',
		'Show cases processed today',
		'Which case has waited longest?',
		'How do I upload a statement?',
	],
	analysis: [
		'Compare to current rate',
		'What is the next step?',
		'How do I export or share this?',
		'Report a wrong number',
	],
	approvals: [
		'Show requests below floor',
		'Oldest pending items',
		'Filter by my team',
		'What does "below floor" mean?',
		'Who can approve?',
	],
	analytics: [
		'Explain this chart',
		'What changed this week?',
		'Show close rate by rep',
		'Who has the most quotes?',
		'What is the average saving?',
	],
	team: [
		'What can each role do?',
		'How do I add a user?',
		'How do I change a role?',
		'How do I deactivate a user?',
		'Who sees ISO profit?',
	],
	settings: [
		'What happens when a quote breaks a limit?',
		'Where are the pricing templates?',
		'How do I add an API key?',
		'Which integrations are on?',
		'How long are statements kept?',
	],
	home: [
		'How do I upload a statement?',
		'Which cases wait on merchants?',
		'Which cases need attention?',
	],
};

const PAGES: Record<string, { title: string; facts?: string[] }> = {
	statements: { title: 'Statements' },
	analysis: { title: 'Analysis' },
	approvals: { title: 'Approval queue' },
	analytics: { title: 'Analytics' },
	team: {
		title: 'Team',
		facts: ['Roles: Sales, Pricing team, Manager, Admin'],
	},
	settings: {
		title: 'Settings',
		facts: [
			'Organisation, partners, revenue configurations, pricing templates, API keys, integrations',
		],
	},
	home: { title: 'Basys' },
};

const suggestionsFor = ({ page, context }: PageContext): string[] => {
	const list = SUGGESTIONS[page] ?? SUGGESTIONS.home;
	return page === 'analysis' && typeof context.rate === 'string'
		? [`Why is the rate ${context.rate}?`, ...list]
		: list;
};

export const pageFromPath = (pathname: string): PageContext => {
	const analysis = pathname.match(/^\/analysis\/([^/]+)/);
	if (analysis) {
		return { page: 'analysis', context: { statement_id: analysis[1] } };
	}
	if (['/', '/reports', '/upload'].includes(pathname)) {
		return { page: 'statements', context: {} };
	}
	if (pathname.startsWith('/config')) return { page: 'settings', context: {} };
	const page = pathname.split('/')[1];
	return { page: page in PAGES ? page : 'home', context: {} };
};

const CANNOT_ANSWER =
	'I cannot answer that from this page. Open a ticket, and the team looks at it with this page and this chat.';
const UNAVAILABLE =
	'The Concierge is not available right now. Open a ticket, and the team looks at it with this page and this chat.';

const Concierge: React.FC = () => {
	const location = useLocation();
	const { user } = useAuth();
	const [open, setOpen] = useState(false);
	const [current, setCurrent] = useState<PageContext>(() =>
		pageFromPath(location.pathname),
	);
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const [ticketDetail, setTicketDetail] = useState<string | null>(null);
	const [ticketId, setTicketId] = useState<string | null>(null);
	const [ticketError, setTicketError] = useState(false);
	const [ask, { isLoading: asking }] = useAskConciergeMutation();
	const [openTicket, { isLoading: filing }] = useOpenConciergeTicketMutation();
	const bodyRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const switchTo = (next: PageContext) => {
		setCurrent(next);
		setMessages([]);
		setTicketDetail(null);
		setTicketId(null);
		setTicketError(false);
	};

	useEffect(() => {
		switchTo(pageFromPath(location.pathname));
	}, [location.pathname]);

	useEffect(() => {
		const onOpen = (event: Event) => {
			const { page, context } = (event as CustomEvent<ConciergeOpenDetail>)
				.detail;
			switchTo({
				page,
				context: { ...pageFromPath(location.pathname).context, ...context },
			});
			setOpen(true);
		};
		window.addEventListener(CONCIERGE_OPEN_EVENT, onOpen);
		return () => window.removeEventListener(CONCIERGE_OPEN_EVENT, onOpen);
	}, [location.pathname]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		bodyRef.current?.scrollTo?.({ top: bodyRef.current.scrollHeight });
	}, [messages, ticketDetail, ticketId]);

	const pageInfo = PAGES[current.page] ?? PAGES.home;
	const title = current.context.title ?? pageInfo.title;
	const { statement_id, facts = pageInfo.facts, ...context } = current.context;
	const conversation = (turns: Message[]) => ({
		page: current.page,
		context,
		statement_id,
		messages: turns
			.slice(-MAX_TURNS)
			.map(({ role, content }) => ({ role, content })),
	});

	const send = async (question: string) => {
		const text = question.trim();
		if (!text || asking) return;
		setInput('');
		const turns: Message[] = [...messages, { role: 'user', content: text }];
		setMessages(turns);
		const result = await ask(conversation(turns));
		const data = 'data' in result ? result.data : undefined;
		const reply: Message =
			data && !data.escalate
				? { role: 'assistant', content: data.answer ?? '' }
				: {
						role: 'assistant',
						content: data ? CANNOT_ANSWER : UNAVAILABLE,
						escalate: true,
					};
		setMessages([...turns, reply]);
	};

	const submitTicket = async () => {
		if (!ticketDetail?.trim()) {
			setTicketError(true);
			return;
		}
		const result = await openTicket({
			...conversation(messages),
			detail: ticketDetail.trim(),
		});
		if ('data' in result && result.data) {
			setTicketDetail(null);
			setTicketId(result.data.id.slice(0, 8).toUpperCase());
		} else {
			setTicketError(true);
		}
	};

	return (
		<>
			{open && (
				<section
					role="dialog"
					aria-modal="false"
					aria-labelledby="concierge-title"
					className="fixed bottom-[84px] right-6 z-[150] flex h-[min(760px,calc(100vh-164px))] w-[400px] max-w-[calc(100%-48px)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_24px_60px_rgba(7,26,64,.25)] max-sm:left-3 max-sm:right-3 max-sm:w-auto max-sm:max-w-none"
				>
					<header className="flex items-center justify-between gap-2 bg-[#071a40] py-2.5 pl-4 pr-3 text-white">
						<div
							id="concierge-title"
							className="min-w-0 text-[15px] font-semibold"
						>
							Concierge
						</div>
						<div className="flex flex-none gap-0.5">
							<button
								type="button"
								onClick={() => switchTo({ ...current })}
								aria-label="New chat"
								title="New chat"
								className={HEADER_BUTTON}
							>
								<Plus className="h-[18px] w-[18px]" aria-hidden="true" />
							</button>
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Minimize the concierge"
								title="Minimize"
								className={HEADER_BUTTON}
							>
								<Minus className="h-[18px] w-[18px]" aria-hidden="true" />
							</button>
						</div>
					</header>

					<div
						ref={bodyRef}
						aria-live="polite"
						className="flex min-h-[160px] flex-1 flex-col gap-2.5 overflow-auto bg-[#f6f6f7] px-4 py-3.5"
					>
						<div className={`${BUBBLE} ${BOT}`}>
							You are on <b>{title}</b>.
							{facts?.length ? (
								<ul className="mt-1.5 list-disc pl-[18px]">
									{facts.map((fact) => (
										<li key={fact}>{fact}</li>
									))}
								</ul>
							) : null}
							<div className="mt-1.5">What do you want to know?</div>
						</div>

						{messages.map((message, index) => (
							<div
								key={index}
								className={`${BUBBLE} whitespace-pre-wrap [overflow-wrap:anywhere] ${message.role === 'user' ? ME : BOT}`}
							>
								{message.content}
								{message.escalate &&
									index === messages.length - 1 &&
									ticketDetail === null &&
									!ticketId && (
										<div className="mt-2.5 flex flex-wrap gap-1.5">
											<button
												type="button"
												onClick={() => setTicketDetail('')}
												className={`${BTN_SM} min-h-9 border-gray-300 bg-white text-primary-700 hover:bg-gray-50`}
											>
												Open a ticket
											</button>
										</div>
									)}
							</div>
						))}

						{asking && (
							<div className="self-center py-0.5 text-xs text-gray-500">
								Concierge is typing…
							</div>
						)}

						{ticketDetail !== null && (
							<div className={`${BUBBLE} ${BOT} flex flex-col gap-2`}>
								<b>Open a ticket</b>
								<dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-2.5 gap-y-0.5 text-xs">
									<dt className="text-gray-500">Page</dt>
									<dd className="m-0">{title}</dd>
									<dt className="text-gray-500">From</dt>
									<dd className="m-0">{user?.name || user?.email}</dd>
								</dl>
								<details>
									<summary>
										The chat is attached ({messages.length} messages)
									</summary>
									<pre className="whitespace-pre-wrap text-[11px]">
										{messages
											.map(
												(m) =>
													`${m.role === 'user' ? 'You' : 'Concierge'}: ${m.content}`,
											)
											.join('\n')}
									</pre>
								</details>
								<label
									htmlFor="concierge-ticket-detail"
									className="text-xs font-medium text-gray-500"
								>
									What went wrong?
								</label>
								<textarea
									id="concierge-ticket-detail"
									value={ticketDetail}
									aria-invalid={ticketError || undefined}
									onChange={(e) => {
										setTicketDetail(e.target.value);
										setTicketError(false);
									}}
									placeholder="For example: the Visa discount on page 2 is $3,953.52, but the analysis shows a different figure."
									className={`${INPUT} min-h-16 resize-y px-4`}
								/>
								{ticketError && (
									<p className="text-xs text-red-600">
										Say what went wrong, then submit the ticket.
									</p>
								)}
								<div className="flex justify-end gap-1.5">
									<button
										type="button"
										onClick={() => setTicketDetail(null)}
										className={`${BTN_SM} border-gray-300 bg-white text-gray-900 hover:bg-gray-100`}
									>
										Cancel
									</button>
									<button
										type="button"
										disabled={filing}
										onClick={submitTicket}
										className={`${BTN_SM} border-transparent bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-60`}
									>
										Submit ticket
									</button>
								</div>
							</div>
						)}

						{ticketId && (
							<div role="status" className={`${BUBBLE} ${BOT}`}>
								Ticket <b>{ticketId}</b> is open{' '}
								<span className="inline-flex items-center rounded-full border border-primary-100 bg-primary-50 px-2 py-1 text-xs font-medium leading-[1.3] text-primary-700">
									Open
								</span>
								. It has this page, this chat and your details.
							</div>
						)}
					</div>

					<footer className="flex flex-col gap-2 border-t border-gray-200 bg-white px-3 py-2.5">
						<div className="flex flex-wrap gap-1.5">
							{suggestionsFor(current).map((suggestion) => (
								<button
									key={suggestion}
									type="button"
									onClick={() => send(suggestion)}
									className="min-h-8 rounded-full border border-gray-300 bg-white px-2.5 py-1 text-left text-xs text-gray-700 hover:border-blue-500 hover:text-[#071a40]"
								>
									{suggestion}
								</button>
							))}
						</div>
						<form
							className="relative flex gap-2"
							onSubmit={(e) => {
								e.preventDefault();
								send(input);
							}}
						>
							<input
								ref={inputRef}
								className={`${INPUT} min-w-0 flex-1 pl-4 pr-12`}
								aria-label="Your question"
								placeholder={
									statement_id
										? 'Ask about this statement'
										: 'Ask about this page'
								}
								autoComplete="off"
								value={input}
								onChange={(e) => setInput(e.target.value)}
							/>
							<button
								type="submit"
								aria-label="Send"
								title="Send"
								disabled={!input.trim() || asking}
								className={`absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full p-0 text-white disabled:cursor-default disabled:opacity-35 ${NAVY}`}
							>
								<ArrowUp
									className="h-4 w-4"
									strokeWidth={2.2}
									aria-hidden="true"
								/>
							</button>
						</form>
					</footer>
				</section>
			)}

			<button
				type="button"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className={`fixed bottom-6 right-6 z-[140] inline-flex h-12 items-center gap-2 rounded-full border-0 pl-3.5 pr-[18px] text-sm font-semibold text-white shadow-[0_10px_30px_rgba(7,26,64,.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white max-sm:bottom-4 max-sm:right-4 max-sm:px-3.5 ${open ? 'bg-[#0b2a66]' : NAVY}`}
			>
				<MessageCircleQuestion className="h-5 w-5" aria-hidden="true" />
				<span className="max-sm:hidden">Concierge</span>
			</button>
		</>
	);
};

export default Concierge;
